import { useEffect } from "react";

const revealSelector = "[data-reveal]";

function getRevealNodes(root) {
  const nodes = [];

  if (root.matches?.(revealSelector)) {
    nodes.push(root);
  }

  return nodes.concat(Array.from(root.querySelectorAll(revealSelector)));
}

function hasRevealNode(node) {
  return (
    node.nodeType === Node.ELEMENT_NODE &&
    (node.matches?.(revealSelector) || node.querySelector?.(revealSelector))
  );
}

export function useReveal(dependency, rootRef) {
  useEffect(() => {
    const root = rootRef?.current || document.body;
    let frame = null;

    if (!("IntersectionObserver" in window)) {
      const revealAll = () => {
        getRevealNodes(root).forEach((node) => node.classList.add("is-visible"));
      };
      const scheduleReveal = () => {
        if (frame !== null) {
          return;
        }

        frame = window.requestAnimationFrame(() => {
          frame = null;
          revealAll();
        });
      };
      const mutationObserver = new MutationObserver((mutations) => {
        if (mutations.some((mutation) => Array.from(mutation.addedNodes).some(hasRevealNode))) {
          scheduleReveal();
        }
      });

      revealAll();
      mutationObserver.observe(root, { childList: true, subtree: true });

      return () => {
        if (frame !== null) {
          window.cancelAnimationFrame(frame);
        }

        mutationObserver.disconnect();
      };
    }

    const fallbackTimers = new Map();

    const revealNode = (node) => {
      node.classList.add("is-visible");
      observer.unobserve(node);

      const timer = fallbackTimers.get(node);

      if (timer) {
        window.clearTimeout(timer);
        fallbackTimers.delete(node);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            revealNode(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }
    );

    const observeRevealNodes = () => {
      frame = null;

      getRevealNodes(root).forEach((node) => {
        if (!node.classList.contains("is-visible")) {
          observer.observe(node);

          if (!fallbackTimers.has(node)) {
            fallbackTimers.set(node, window.setTimeout(() => revealNode(node), 900));
          }
        }
      });
    };

    const scheduleObserve = () => {
      if (frame !== null) {
        return;
      }

      frame = window.requestAnimationFrame(observeRevealNodes);
    };
    const mutationObserver = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => Array.from(mutation.addedNodes).some(hasRevealNode))) {
        scheduleObserve();
      }
    });

    scheduleObserve();
    mutationObserver.observe(root, { childList: true, subtree: true });

    return () => {
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }

      observer.disconnect();
      mutationObserver.disconnect();
      fallbackTimers.forEach((timer) => window.clearTimeout(timer));
      fallbackTimers.clear();
    };
  }, [dependency, rootRef]);
}
